import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  CheckCircle, Circle, ArrowRight, X, User, Users, 
  FileText, Send, Bell, Sparkles, Gift
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  user: User,
  users: Users,
  'file-text': FileText,
  send: Send,
  bell: Bell
};

const OnboardingChecklist = ({ onClose }) => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [minimized, setMinimized] = useState(false);

  useEffect(() => {
    loadProgress();
  }, []);

  const loadProgress = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/onboarding/progress`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setProgress(data);
    } catch (error) {
      console.error('Error loading onboarding:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/onboarding/skip`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      onClose?.();
    } catch (error) {
      console.error('Error skipping onboarding:', error);
    }
  };

  const handleStepClick = (step) => {
    if (!step.completed && step.action_url) {
      navigate(step.action_url);
    }
  };

  if (loading || !progress) {
    return null;
  }

  if (progress.is_complete) {
    return null;
  }

  // Minimized view
  if (minimized) {
    return (
      <div 
        className="fixed bottom-4 right-4 z-50 cursor-pointer"
        onClick={() => setMinimized(false)}
      >
        <div className="bg-gradient-to-r from-emerald-600 to-cyan-600 text-white px-4 py-3 rounded-xl shadow-lg flex items-center gap-3 hover:scale-105 transition-transform">
          <div className="relative">
            <Sparkles className="h-5 w-5" />
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-400 text-[10px] font-bold rounded-full flex items-center justify-center text-amber-900">
              {progress.total_steps - progress.completed_steps.length}
            </span>
          </div>
          <span className="font-medium">Terminer la configuration</span>
          <div className="w-16 h-2 bg-white/30 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white rounded-full transition-all"
              style={{ width: `${progress.progress_percent}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card className="fixed bottom-4 right-4 z-50 w-96 shadow-2xl border-0 overflow-hidden" data-testid="onboarding-checklist">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-cyan-600 p-4 text-white">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            <span className="font-semibold">Démarrage rapide</span>
          </div>
          <div className="flex items-center gap-1">
            <button 
              onClick={() => setMinimized(true)}
              className="p-1 hover:bg-white/20 rounded"
              title="Réduire"
            >
              <span className="text-lg">−</span>
            </button>
            <button 
              onClick={handleSkip}
              className="p-1 hover:bg-white/20 rounded"
              title="Fermer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="flex items-center gap-3">
          <div className="flex-1 h-2 bg-white/30 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white rounded-full transition-all duration-500"
              style={{ width: `${progress.progress_percent}%` }}
            />
          </div>
          <span className="text-sm font-medium">{progress.progress_percent}%</span>
        </div>
        
        {/* Points */}
        <div className="flex items-center gap-2 mt-2 text-sm text-white/80">
          <Gift className="h-4 w-4" />
          <span>{progress.points_earned} / {progress.max_points} points</span>
        </div>
      </div>

      {/* Steps */}
      <CardContent className="p-0 max-h-80 overflow-y-auto">
        {progress.all_steps.map((step, index) => {
          const Icon = iconMap[step.icon] || Circle;
          const isNext = progress.next_step?.id === step.id;
          
          return (
            <div
              key={step.id}
              className={`flex items-start gap-3 p-4 border-b border-slate-100 last:border-0 transition-colors cursor-pointer ${
                step.completed 
                  ? 'bg-emerald-50/50' 
                  : isNext 
                  ? 'bg-emerald-50 hover:bg-emerald-100' 
                  : 'hover:bg-slate-50'
              }`}
              onClick={() => handleStepClick(step)}
              data-testid={`onboarding-step-${step.id}`}
            >
              {/* Icon/Check */}
              <div className={`mt-0.5 ${step.completed ? 'text-emerald-500' : isNext ? 'text-emerald-500' : 'text-slate-300'}`}>
                {step.completed ? (
                  <CheckCircle className="h-6 w-6" />
                ) : (
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    isNext ? 'border-emerald-500 bg-emerald-100' : 'border-slate-300'
                  }`}>
                    <Icon className={`h-3 w-3 ${isNext ? 'text-emerald-500' : 'text-slate-400'}`} />
                  </div>
                )}
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`font-medium ${step.completed ? 'text-emerald-700 line-through' : 'text-slate-800'}`}>
                    {step.title}
                  </span>
                  {isNext && (
                    <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">
                      Suivant
                    </Badge>
                  )}
                  <Badge variant="outline" className="text-[10px] text-slate-500">
                    +{step.points} pts
                  </Badge>
                </div>
                <p className={`text-sm mt-0.5 ${step.completed ? 'text-emerald-600/70' : 'text-slate-500'}`}>
                  {step.description}
                </p>
                
                {/* Action button for next step */}
                {isNext && !step.completed && (
                  <Button
                    size="sm"
                    className="mt-2 bg-emerald-600 hover:bg-emerald-700 text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(step.action_url);
                    }}
                  >
                    {step.action_label}
                    <ArrowRight className="h-3 w-3 ml-1" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </CardContent>

      {/* Footer */}
      <div className="p-3 bg-slate-50 border-t border-slate-100">
        <p className="text-xs text-slate-500 text-center">
          Complétez toutes les étapes pour débloquer des fonctionnalités bonus ✨
        </p>
      </div>
    </Card>
  );
};

export default OnboardingChecklist;
